/**
 * @file    FiltersTab.tsx
 * @scope   VIEW
 * @summary The "Filters" tab of a field card: lists the field's advanced filters with their AND/OR
 *          operator and the add/edit/remove affordances (capped at MAX_FILTERS).
 * @consumes FieldsManager models, operator options resolver
 * @produces `FiltersTab` component, `MAX_FILTERS`, re-exported filter types
 * @sideEffects None — presentational; edits bubble up via callbacks.
 * @notes Editing a specific filter is delegated to FiltersSettingsManager via `onEditFilter`.
 */
import React from 'react';
import { Icon, Skeleton, SkeletonVariants } from '@cx/ui';
import { FieldFilter, FilterLogicalOperator } from 'models/Enterprise/FieldsManagerModel';
import { AdvancedFilterFieldConfig } from 'models/WidgetModel';
import { getOperatorsForField } from './mockFiltersOptions';

/** Re-export types for existing consumers */
export type { FieldFilter, FilterLogicalOperator };

/** Maximum number of advanced filters allowed per field. */
export const MAX_FILTERS = 10;

interface FiltersTabProps {
  /** Currently configured filters for this field */
  filters: FieldFilter[];
  /** Available filter fields from Config.Filters.AdvancedFilters */
  availableFilterFields: AdvancedFilterFieldConfig[];
  /** Whether the entire filters section is disabled */
  disabled?: boolean;
  /** Shows a skeleton placeholder while filter data is loading */
  isLoading?: boolean;
  /** Called when user clicks Edit on a filter or Add Filter link */
  onEditFilter: (filterId: string | null) => void;
  /** Called when the user removes an existing filter */
  onRemoveFilter: (filterId: string) => void;
}

/**
 * Extracts display parts for a filter summary.
 */
const getFilterParts = (
  filter: FieldFilter,
  availableFilterFields: AdvancedFilterFieldConfig[]
) => {
  const fieldName = filter.filterName || 'No field selected';

  if (!filter.filterGuid) return { fieldName, operatorLabel: '', valuesLabel: '' };

  const field = availableFilterFields.find((f) => f.attributeGuid === filter.filterGuid);
  const operators = getOperatorsForField(field);
  const operatorLabel =
    operators.find((o) => o.value === filter.operator)?.label || filter.operator || '—';

  const valuesLabel =
    filter.selectedValues.length > 0
      ? filter.selectedValues.map((v) => v.label).join(', ')
      : 'no values selected';

  return { fieldName, operatorLabel, valuesLabel };
};

/** Skeleton placeholder: one block per filter (min. 1) plus the "Add Filter" link. */
const renderFiltersSkeleton = (count: number) => (
  <div className="filters-tab filters-tab__skeleton">
    {Array.from({ length: Math.max(count, 1) }, (_, i) => (
      <Skeleton key={i} variant={SkeletonVariants.rectangular} height="56px" />
    ))}
    <Skeleton variant={SkeletonVariants.text} width="70px" />
  </div>
);

/**
 * FiltersTab — Summary view of configured filters.
 *
 * Displays each filter as a bullet point with formatted description,
 * plus Edit and Delete action buttons. An "Add Filter" link at the bottom
 * navigates to the full FiltersSettingsManager view.
 */
const FiltersTab: React.FC<FiltersTabProps> = ({
  filters,
  availableFilterFields,
  disabled = false,
  isLoading = false,
  onEditFilter,
  onRemoveFilter,
}) => {
  if (isLoading) return renderFiltersSkeleton(filters.length);

  const canAddMore = filters.length < MAX_FILTERS;

  return (
    <div className={`filters-tab${disabled ? ' filters-tab--disabled' : ''}`}>
      {filters.length === 0 && (
        <p className="filters-tab__empty">No advanced filters configured.</p>
      )}

      {filters.length > 0 && (
        <ul className="filters-tab__list">
          {filters.map((filter, idx) => {
            const { fieldName, operatorLabel, valuesLabel } = getFilterParts(
              filter,
              availableFilterFields
            );
            return (
              <React.Fragment key={filter.id}>
                {idx > 0 && (
                  <li className="filters-tab__separator">
                    <span
                      className={`filters-tab__logic-badge filters-tab__logic-badge--${(
                        filter.logicalOperator ?? 'AND'
                      ).toLowerCase()}`}
                    >
                      {filter.logicalOperator ?? 'AND'}
                    </span>
                  </li>
                )}
                <li className="filters-tab__item">
                  <div className="filters-tab__content">
                    <div className="filters-tab__line-field">
                      <span className="filters-tab__field-name">{fieldName}</span>
                    </div>
                    {operatorLabel && (
                      <div className="filters-tab__line-operator">
                        <span className="filters-tab__operator">{operatorLabel}</span>
                      </div>
                    )}
                    {valuesLabel && (
                      <div className="filters-tab__line-values">
                        <span className="filters-tab__values">{valuesLabel}</span>
                      </div>
                    )}
                  </div>
                  <div className="filters-tab__actions">
                    <button
                      type="button"
                      className="filters-tab__action-btn filters-tab__action-btn--edit"
                      onClick={() => onEditFilter(filter.id)}
                      title="Edit filter"
                      disabled={disabled}
                    >
                      <Icon name="system-edit" />
                    </button>
                    <button
                      type="button"
                      className="filters-tab__action-btn filters-tab__action-btn--delete"
                      onClick={() => onRemoveFilter(filter.id)}
                      title="Remove filter"
                      disabled={disabled}
                    >
                      <Icon name="system-delete" />
                    </button>
                  </div>
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      )}

      {canAddMore && (
        <div className="filters-tab__add-container">
          <a
            href="#"
            className={`manage-fields-link filters-tab__add${
              disabled ? ' filters-tab__add--disabled' : ''
            }`}
            onClick={(e) => {
              e.preventDefault();
              if (!disabled) onEditFilter(null);
            }}
          >
            Add Filter
          </a>
        </div>
      )}
    </div>
  );
};

export default FiltersTab;
